import { useState, useEffect, useRef } from "react";
import { NavItem, useCollapsible } from "playbook-ui";
import { useNavigate, useLocation } from "react-router-dom";
import { KitsNavItem, kitsType } from "./NavComponents/KitsNavComponent";
import { SideBarNavItems } from "./MenuData/SidebarNavItems";
import { OtherNavItems } from "./NavComponents/OtherNavComponent";
import { CollapsedHoverNav } from "./CollapsedHoverNav";
import { navigateSite } from "../../utils/siteNavigation";

export const TopLevelNavItem = ({
  dark,
  type,
  kits,
  kit,
  category,
  collapsibles,
  getting_started,
  design_guidelines,
  whats_new,
  global_props_and_tokens,
  sidebarCollapsed = false,
}: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentURL = location.pathname + location.search;
  //hook into collapsible logic for top level item
  const topLevelCollapsibles = SideBarNavItems.map(() => useCollapsible());
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const itemElsRef = useRef<Record<string, HTMLElement | null>>({});
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openHoverNav = (key: string) => {
    clearCloseTimeout();
    setHoveredKey(key);
  };

  const scheduleCloseHoverNav = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredKey(null);
    }, 120);
  };

  useEffect(() => {
    if (!sidebarCollapsed) {
      clearCloseTimeout();
      setHoveredKey(null);
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    clearCloseTimeout();
    setHoveredKey(null);
  }, [location]);

  useEffect(() => {
    return () => clearCloseTimeout();
  }, []);

  //logic to make it so no navigation if already on that page(prevent unneeded rerenders)
  const TopLevelLink = (link) => {
    if (link === "/kits") {
      const kitsLink = `/kits${kitsType(type) ? `?type=${kitsType(type)}` : ""}`;
      return currentURL === kitsLink ? "" : kitsLink;
    } else {
      return currentURL === link ? "" : link;
    }
  };

  //set up toggling for top level item
  const handleComponentsClick = (item, index) => {
    const linkPath = TopLevelLink(SideBarNavItems[index].link);
    if (linkPath && navigate) {
      navigateSite(navigate, linkPath);
    }
    
    topLevelCollapsibles.forEach(([, , setCollapsed], idx) => {
      if (idx === index) {
        setCollapsed(false);
      } else {
        setCollapsed(true);
      }
    });
    //return true at end to disable default collapsible behavior
    return true;
  };

  const activeTopLevel = (link, hasChildren) => {
    // Special handling for items with children
    if (hasChildren) {
      // Top level items with children should be active ONLY on their exact main page
      if (link === "/kits") {
        return currentURL === "/kits" || currentURL.startsWith("/kits?");
      }
      if (link === "/changelog") {
        return currentURL === "/changelog" || currentURL === "/changelog?";
      }
      if (link === "/guides/getting_started") {
        return currentURL === "/guides/getting_started" || currentURL === "/guides/getting_started?";
      }
      if (link === "/guides/design_guidelines") {
        return currentURL === "/guides/design_guidelines" || currentURL === "/guides/design_guidelines?";
      }
      if (link === "/global_props") {
        return currentURL === "/global_props" || currentURL === "/global_props?";
      }
      if (link === "/global_event_props") {
        return currentURL === "/global_event_props" || currentURL === "/global_event_props?";
      }
      if (link === "/tokens") {
        return currentURL === "/tokens" || currentURL === "/tokens?";
      }
      
      // For other items with children, only active on exact match
      return currentURL === link || currentURL.startsWith(`${link}?`);
    }
    
    // Items without children can use startsWith
    return currentURL.startsWith(link);
  };
  
  const shouldExpandTopLevel = (link) => {
    // Expand if current URL is under this section
    if (link === "/kits") {
      return currentURL.startsWith("/kits") || currentURL.startsWith("/kit_category");
    }
    if (link === "/changelog") {
      return currentURL.startsWith("/changelog");
    }
    if (link === "/guides/getting_started") {
      return currentURL.startsWith("/guides/getting_started");
    }
    if (link === "/guides/design_guidelines") {
      return currentURL.startsWith("/guides/design_guidelines");
    }
    if (link === "/global_props") {
      return currentURL.startsWith("/global_props");
    }
    if (link === "/global_event_props") {
      return currentURL.startsWith("/global_event_props");
    }
    if (link === "/tokens") {
      return currentURL.startsWith("/tokens");
    }
    
    return currentURL.startsWith(link);
  };

  const renderSubmenu = (name: string, parentIndex: number, updateTopLevelNav: (index: number) => void) => {
    if (name === "Components") {
      return (
        <>
          {kits.map((link, index) => (
            <KitsNavItem
              link={link}
              key={`kits-nav-item-${index}`}
              kitIndex={index}
              collapsibles={collapsibles}
              category={category}
              type={type}
              dark={dark}
              kit={kit}
              updateTopLevelNav={updateTopLevelNav}
              parentIndex={parentIndex}
            />
          ))}
        </>
      );
    }

    return (
      <OtherNavItems
        name={name}
        dark={dark}
        updateTopLevelNav={updateTopLevelNav}
        parentIndex={parentIndex}
        getting_started={getting_started}
        design_guidelines={design_guidelines}
        whats_new={whats_new}
        global_props_and_tokens={global_props_and_tokens}
      />
    );
  };

  //extract render logic out of return for better performance
  const renderTopItems = (name, key, children, leftIcon, link, i) => {
    const [collapsed] = topLevelCollapsibles[i];

    //check if on current page for initial collapse state
    const onCurrentPage = () => {
      return shouldExpandTopLevel(link);
    };

    //use state to handle toggle logic to make sure both main click and right icon click works as expected
    const [toggleTopNav, setToggleTopNav] = useState(
      onCurrentPage() ? false : true
    );

    //sync collapsed state with collapsible hook and re-check if should be expanded on location change
    useEffect(() => {
      // If we should be expanded based on current URL, don't collapse
      if (shouldExpandTopLevel(link)) {
        setToggleTopNav(false);
      } else {
        setToggleTopNav(collapsed);
      }
    }, [collapsed, location]);

    //right icon click for top level item
    const handleComponentsIconClick = (i) => {
      topLevelCollapsibles.forEach(([, toggle], idx) => {
        if (idx === i) {
          toggleTopNav === true
            ? setToggleTopNav(false)
            : setToggleTopNav(true);
        }
      });
    };

    //callback function so top level nav item stays toggled opwn if child is clicked
    const updateTopLevelNav = (index) => {
      topLevelCollapsibles.forEach((collapsible, i) => {
        const [, , setCollapsed] = collapsible;

        if (i !== index) {
          setCollapsed(true);
        } else {
          setCollapsed(false);
        }
      });
    };

    const hoverHandlers = sidebarCollapsed
      ? {
          onMouseEnter: (event: { currentTarget: HTMLElement }) => {
            itemElsRef.current[key] = event.currentTarget;
            openHoverNav(key);
          },
          onMouseLeave: () => scheduleCloseHoverNav(),
        }
      : undefined;

    return (
      <NavItem
        active={sidebarCollapsed ? shouldExpandTopLevel(link) : activeTopLevel(link, children)}
        collapsed={!sidebarCollapsed && children && toggleTopNav}
        collapsible={!sidebarCollapsed && children}
        collapsibleTrail={!sidebarCollapsed && children}
        cursor="pointer"
        dark={dark}
        fontSize="small"
        fontWeight="bolder"
        htmlOptions={hoverHandlers as any}
        iconLeft={leftIcon}
        iconRight={!sidebarCollapsed && children && ["plus", "minus"]}
        key={key}
        marginY="none"
        onClick={() => handleComponentsClick(key, i)}
        onIconRightClick={!sidebarCollapsed && children && (() => handleComponentsIconClick(i))}
        paddingY="xxs"
        text={name}
      >
        {children && !sidebarCollapsed && renderSubmenu(name, i, updateTopLevelNav)}
      </NavItem>
    );
  };

  const hoveredItem = SideBarNavItems.find((item) => item.key === hoveredKey);
  const hoveredIndex = SideBarNavItems.findIndex((item) => item.key === hoveredKey);

  const updateTopLevelNavFromHover = (index: number) => {
    topLevelCollapsibles.forEach((collapsible, i) => {
      const [, , setCollapsed] = collapsible;
      if (i !== index) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    });
    setHoveredKey(null);
  };

  return (
    <>
      {SideBarNavItems.map(({ name, key, children, leftIcon, link }, i) => {
        return renderTopItems(name, key, children, leftIcon, link, i);
      })}
      {sidebarCollapsed && hoveredItem && hoveredKey && (
        <CollapsedHoverNav
          anchorEl={itemElsRef.current[hoveredKey]}
          dark={dark}
          onMouseEnter={() => openHoverNav(hoveredKey)}
          onMouseLeave={scheduleCloseHoverNav}
          onTitleClick={() => {
            handleComponentsClick(hoveredItem.key, hoveredIndex);
            setHoveredKey(null);
          }}
          title={hoveredItem.name}
        >
          {hoveredItem.children
            ? renderSubmenu(hoveredItem.name, hoveredIndex, updateTopLevelNavFromHover)
            : null}
        </CollapsedHoverNav>
      )}
    </>
  );
};
